import { useState, useMemo } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { Check, Inbox, User } from 'lucide-react';
import type {
  CalendarAssignmentRow,
  CalendarTaskRow,
  CalendarWarrantyVisitRow,
} from '@gutterinv/shared';
import { taskStyle } from '../lib/task-styles.js';
import { WARRANTY_STYLE } from '../lib/warranty-styles.js';
import { Card } from './ui/card.js';
import { cn } from '../lib/utils.js';
import { useEscapeKey } from '../hooks/useEscapeKey.js';
import { AssignmentEditModal } from './AssignmentEditModal.js';
import {
  DAY_LABELS,
  daySpanInclusive,
  isPostInstallStatus,
  mixWithWhite,
  toISO,
  isToday,
} from '../lib/calendar-dates.js';
import type { DropTarget } from '../lib/calendar-drop.js';
import type { CalendarDragData } from '../hooks/useCalendarDnd.js';
import { packLanes } from '../lib/calendar-lanes.js';

// Phase 2.2: calendar rows are PEOPLE, not crews. One assignment with N
// people fans out across N person rows. The synthetic NO_PEOPLE_ROW catches
// any assignment whose assignment_people set is empty so nothing disappears.
export interface PersonRow {
  id: string;
  name: string;
}

export const NO_PEOPLE_ROW_ID = '__none__';

function gridTemplate(dayCount: number): string {
  return `128px repeat(${dayCount}, minmax(0, 1fr))`;
}

export function CalendarWeekView({
  days,
  people,
  assignmentIndex,
  tasksByPersonDay,
  onTaskClick,
  warrantyByPersonDay,
  onWarrantyClick,
}: {
  days: Date[];
  people: PersonRow[];
  assignmentIndex: Map<string, CalendarAssignmentRow[]>;
  tasksByPersonDay: Map<string, CalendarTaskRow[]>;
  onTaskClick: (task: CalendarTaskRow) => void;
  warrantyByPersonDay: Map<string, CalendarWarrantyVisitRow[]>;
  onWarrantyClick: (visit: CalendarWarrantyVisitRow) => void;
}) {
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  // Fan-out highlights: hovering one chip/pill rings all siblings of the same
  // assignment/task/warranty across the assignees' rows.
  const [hoveredAssignmentId, setHoveredAssignmentId] = useState<string | null>(null);
  const [hoveredTaskId, setHoveredTaskId] = useState<string | null>(null);
  const [hoveredWarrantyId, setHoveredWarrantyId] = useState<string | null>(null);

  const firstISO = toISO(days[0]!);
  const lastISO = toISO(days[days.length - 1]!);

  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        {/* HEADER: person column + day labels */}
        <div
          className="grid border-b border-border"
          style={{ gridTemplateColumns: gridTemplate(days.length), minWidth: 128 + days.length * 90 }}
        >
          <div className="sticky left-0 z-10 border-r border-border bg-muted/50 px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
            People
          </div>
          {days.map((d) => (
            <div
              key={toISO(d)}
              className={cn(
                'px-2 py-2 text-center text-xs font-medium',
                isToday(d) ? 'bg-primary/10 text-primary' : 'text-muted-foreground',
                d.getDay() === 0 || d.getDay() === 6 ? 'bg-muted/30' : '',
              )}
            >
              <div className="uppercase tracking-wider">
                {DAY_LABELS[d.getDay() === 0 ? 6 : d.getDay() - 1]}
              </div>
              <div
                className={cn(
                  'mt-0.5 text-sm font-semibold',
                  isToday(d)
                    ? 'inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground'
                    : '',
                )}
              >
                {d.getDate()}
              </div>
            </div>
          ))}
        </div>

        {/* BODY: one CSS grid per person row */}
        {people.map((person, idx) => (
          <PersonLaneRow
            key={person.id}
            person={person}
            days={days}
            firstISO={firstISO}
            lastISO={lastISO}
            assignmentIndex={assignmentIndex}
            tasksByPersonDay={tasksByPersonDay}
            onTaskClick={onTaskClick}
            warrantyByPersonDay={warrantyByPersonDay}
            onWarrantyClick={onWarrantyClick}
            selectedJobId={selectedJobId}
            onSelectJob={setSelectedJobId}
            hoveredAssignmentId={hoveredAssignmentId}
            onHoverAssignment={setHoveredAssignmentId}
            hoveredTaskId={hoveredTaskId}
            onHoverTask={setHoveredTaskId}
            hoveredWarrantyId={hoveredWarrantyId}
            onHoverWarranty={setHoveredWarrantyId}
            isLast={idx === people.length - 1}
          />
        ))}
      </div>
      {people.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Inbox className="mb-2 h-8 w-8 text-muted-foreground/40" />
          <div className="text-sm font-medium text-muted-foreground">
            No people configured
          </div>
          <div className="text-xs text-muted-foreground">
            Add people in Settings to start scheduling.
          </div>
        </div>
      )}
    </Card>
  );
}

// Per (person, day) drop target. Hooks can't run in a .map, so each backdrop
// cell is its own component.
function WeekDayCell({
  iso,
  person,
  isNoPeopleRow,
  isWeekend,
  isTodayDay,
  gridColumn,
  totalRows,
}: {
  iso: string;
  person: PersonRow;
  isNoPeopleRow: boolean;
  isWeekend: boolean;
  isTodayDay: boolean;
  gridColumn: number;
  totalRows: number;
}) {
  const isPast = iso < toISO(new Date());
  const { setNodeRef, isOver } = useDroppable({
    id: `pd-${person.id}-${iso}`,
    data: {
      kind: 'person-day',
      iso,
      personId: person.id,
      personName: person.name,
      isNoPeopleRow,
    } satisfies DropTarget,
    disabled: isPast,
  });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'transition-colors',
        isWeekend ? 'bg-muted/20' : '',
        isTodayDay ? 'bg-primary/5' : '',
        isOver && 'bg-primary/15 ring-2 ring-inset ring-primary/40',
      )}
      style={{ gridColumn, gridRow: `1 / span ${totalRows}` }}
    />
  );
}

// Draggable scheduled-task pill (week). id is person-scoped because a
// multi-assignee task fans out across rows (ids must be unique).
function WeekTaskPill({
  task,
  personId,
  onClick,
  onHover,
  highlighted,
}: {
  task: CalendarTaskRow;
  personId: string;
  onClick: () => void;
  onHover: (id: string | null) => void;
  highlighted: boolean;
}) {
  const st = taskStyle(task.taskType);
  const { setNodeRef, listeners, attributes, isDragging } = useDraggable({
    id: `task-${task.id}-${personId}`,
    data: {
      payload: { kind: 'task-reassign', taskId: task.id, personIds: task.people.map((p) => p.id) },
      overlayLabel: st.label,
    } satisfies CalendarDragData,
  });
  return (
    <button
      type="button"
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={onClick}
      onMouseEnter={() => onHover(task.id)}
      onMouseLeave={() => onHover(null)}
      title={st.label}
      className={cn(
        'flex items-center gap-1 truncate rounded border px-1.5 py-1 text-[0.6875rem] font-medium cursor-grab active:cursor-grabbing',
        st.chip,
        task.completedAt && 'line-through opacity-60',
      )}
      style={{
        touchAction: 'none',
        opacity: isDragging ? 0.4 : undefined,
        boxShadow: highlighted ? `0 0 0 1.5px ${st.dot}` : undefined,
      }}
    >
      <st.Icon className="h-3 w-3 shrink-0" />
      <span className="truncate">{st.label}</span>
    </button>
  );
}

function PersonLaneRow({
  person,
  days,
  firstISO,
  lastISO,
  assignmentIndex,
  tasksByPersonDay,
  onTaskClick,
  warrantyByPersonDay,
  onWarrantyClick,
  selectedJobId,
  onSelectJob,
  hoveredAssignmentId,
  onHoverAssignment,
  hoveredTaskId,
  onHoverTask,
  hoveredWarrantyId,
  onHoverWarranty,
  isLast,
}: {
  person: PersonRow;
  days: Date[];
  firstISO: string;
  lastISO: string;
  assignmentIndex: Map<string, CalendarAssignmentRow[]>;
  tasksByPersonDay: Map<string, CalendarTaskRow[]>;
  onTaskClick: (task: CalendarTaskRow) => void;
  warrantyByPersonDay: Map<string, CalendarWarrantyVisitRow[]>;
  onWarrantyClick: (visit: CalendarWarrantyVisitRow) => void;
  selectedJobId: string | null;
  onSelectJob: (id: string | null) => void;
  hoveredAssignmentId: string | null;
  onHoverAssignment: (id: string | null) => void;
  hoveredTaskId: string | null;
  onHoverTask: (id: string | null) => void;
  hoveredWarrantyId: string | null;
  onHoverWarranty: (id: string | null) => void;
  isLast: boolean;
}) {
  const isNoPeopleRow = person.id === NO_PEOPLE_ROW_ID;

  // Dedupe + position chips for this person row. Collapse multi-day spans to
  // one chip per assignment, clamp to the window, then lane-pack overlaps.
  const laidOutChips = useMemo(() => {
    const seen = new Map<string, CalendarAssignmentRow>();
    for (const d of days) {
      const list = assignmentIndex.get(`${person.id}:${toISO(d)}`);
      if (!list) continue;
      for (const a of list) {
        if (!seen.has(a.id)) seen.set(a.id, a);
      }
    }
    const isoToCol = new Map<string, number>();
    days.forEach((d, i) => isoToCol.set(toISO(d), i));
    const chips = [...seen.values()].map((a) => {
      const clampedStart = a.assignedDateStart < firstISO ? firstISO : a.assignedDateStart;
      const clampedEnd = a.assignedDateEnd > lastISO ? lastISO : a.assignedDateEnd;
      return {
        assignment: a,
        startCol: isoToCol.get(clampedStart) ?? 0,
        endCol: isoToCol.get(clampedEnd) ?? days.length - 1,
        extendsLeft: a.assignedDateStart < firstISO,
        extendsRight: a.assignedDateEnd > lastISO,
      };
    });
    return packLanes(chips);
  }, [days, person.id, assignmentIndex, firstISO, lastISO]);

  const laneCount = laidOutChips.reduce((max, c) => Math.max(max, c.lane + 1), 1);
  const totalRows = laneCount + 1;

  return (
    <div
      className={cn('grid', !isLast && 'border-b border-border')}
      style={{
        gridTemplateColumns: gridTemplate(days.length),
        gridTemplateRows: `repeat(${laneCount}, minmax(56px, auto)) auto`,
        minWidth: 128 + days.length * 90,
      }}
    >
      {/* Sticky person name spans all lane rows */}
      <div
        className="sticky left-0 z-10 border-r border-border bg-background px-3 py-3"
        style={{ gridRow: `1 / span ${totalRows}` }}
      >
        <div className="flex items-center gap-2">
          <User className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span
            className={cn(
              'text-sm font-semibold',
              isNoPeopleRow ? 'italic text-muted-foreground' : 'text-foreground',
            )}
          >
            {person.name}
          </span>
        </div>
      </div>

      {/* Day backdrops — each spans all lane rows and owns the drop target. */}
      {days.map((d, i) => (
        <WeekDayCell
          key={toISO(d)}
          iso={toISO(d)}
          person={person}
          isNoPeopleRow={isNoPeopleRow}
          isWeekend={d.getDay() === 0 || d.getDay() === 6}
          isTodayDay={isToday(d)}
          gridColumn={i + 2}
          totalRows={totalRows}
        />
      ))}

      {/* Chips — one per assignment, positioned with grid placement. With
          pointer-based DnD, collision resolves against the day-cell droppable
          rects (not DOM stacking), so chips no longer opt out of pointer events. */}
      {laidOutChips.map((chip) => {
        const span = chip.endCol - chip.startCol + 1;
        return (
          <div
            key={chip.assignment.id}
            style={{
              gridColumn: `${chip.startCol + 2} / span ${span}`,
              gridRow: chip.lane + 1,
              alignSelf: 'center',
              zIndex: 1,
              margin: '0 4px',
            }}
          >
            <JobBlock
              assignment={chip.assignment}
              personId={person.id}
              weekKey={firstISO}
              color={chip.assignment.crew.color}
              isSelected={selectedJobId === chip.assignment.id}
              isHighlighted={hoveredAssignmentId === chip.assignment.id}
              onHover={onHoverAssignment}
              onSelect={() =>
                onSelectJob(selectedJobId === chip.assignment.id ? null : chip.assignment.id)
              }
              onDeselect={() => onSelectJob(null)}
              extendsLeft={chip.extendsLeft}
              extendsRight={chip.extendsRight}
            />
          </div>
        );
      })}

      {/* Scheduled-task + warranty pills — appended bottom row per day column. */}
      {days.map((d, i) => {
        const iso = toISO(d);
        const dayTasks = tasksByPersonDay.get(`${person.id}:${iso}`) ?? [];
        const dayWarranty = warrantyByPersonDay.get(`${person.id}:${iso}`) ?? [];
        if (dayTasks.length === 0 && dayWarranty.length === 0) return null;
        return (
          <div
            key={`pill-${iso}`}
            className="flex flex-col gap-1"
            style={{
              gridColumn: i + 2,
              gridRow: totalRows,
              alignSelf: 'end',
              zIndex: 1,
              margin: '0 4px 4px',
            }}
          >
            {dayTasks.map((t) => (
              <WeekTaskPill
                key={t.id}
                task={t}
                personId={person.id}
                onClick={() => onTaskClick(t)}
                onHover={onHoverTask}
                highlighted={hoveredTaskId === t.id}
              />
            ))}
            {dayWarranty.map((v) => {
              const title = `Warranty — ${v.customerName} (${v.address})`;
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => onWarrantyClick(v)}
                  onMouseEnter={() => onHoverWarranty(v.id)}
                  onMouseLeave={() => onHoverWarranty(null)}
                  title={title}
                  className={cn(
                    'flex items-center gap-1 truncate rounded border px-1.5 py-1 text-[0.6875rem] font-medium',
                    WARRANTY_STYLE.chip,
                    v.completedAt && 'line-through opacity-60',
                  )}
                  style={{
                    boxShadow:
                      hoveredWarrantyId === v.id ? `0 0 0 1.5px ${WARRANTY_STYLE.dot}` : undefined,
                  }}
                >
                  <WARRANTY_STYLE.Icon className="h-3 w-3 shrink-0" />
                  <span className="truncate">{WARRANTY_STYLE.label}</span>
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function JobBlock({
  assignment,
  personId,
  weekKey,
  color,
  isSelected,
  isHighlighted,
  onHover,
  onSelect,
  onDeselect,
  extendsLeft,
  extendsRight,
}: {
  assignment: CalendarAssignmentRow;
  personId: string;
  /** This row's window start — disambiguates the same assignment shown in BOTH
   *  panes of the 2-week view (which share one DndContext). */
  weekKey: string;
  color: string;
  isSelected: boolean;
  isHighlighted: boolean;
  onHover: (id: string | null) => void;
  onSelect: () => void;
  onDeselect: () => void;
  extendsLeft: boolean;
  extendsRight: boolean;
}) {
  useEscapeKey(isSelected, onDeselect);

  const isInstalled = isPostInstallStatus(assignment.job.status);
  const isEstimate = assignment.purpose === 'estimate';
  const isCompleted = assignment.completedAt !== null;
  const isCompletedEstimate = isEstimate && isCompleted;
  const draggable = !isInstalled && !isCompletedEstimate;
  const peopleCount = assignment.people.length;

  // id is person- AND week-scoped — the same assignment fans out across N
  // person rows and can appear in both 2-week panes.
  const { setNodeRef, listeners, attributes, isDragging } = useDraggable({
    id: `assignment-${assignment.id}-${personId}-${weekKey}`,
    data: {
      payload: {
        kind: 'reassign',
        jobId: assignment.job.id,
        purpose: assignment.purpose,
        oldAssignmentId: assignment.id,
        durationDays: daySpanInclusive(assignment.assignedDateStart, assignment.assignedDateEnd) - 1,
      },
      overlayLabel: assignment.job.displayName,
    } satisfies CalendarDragData,
    disabled: !draggable,
  });

  return (
    <>
      <div
        ref={setNodeRef}
        {...listeners}
        {...attributes}
        onClick={onSelect}
        onMouseEnter={() => onHover(assignment.id)}
        onMouseLeave={() => onHover(null)}
        className={cn(
          'max-w-full overflow-hidden px-2 py-1.5 text-left transition-shadow hover:shadow-md',
          extendsLeft ? '' : 'rounded-l-md',
          extendsRight ? '' : 'rounded-r-md',
          isInstalled
            ? 'opacity-50 cursor-pointer'
            : draggable
              ? 'cursor-grab active:cursor-grabbing'
              : 'cursor-pointer',
          isCompleted && !isInstalled && 'opacity-60',
        )}
        style={{
          backgroundColor: isEstimate ? 'transparent' : mixWithWhite(color, 0.16),
          border: isEstimate ? `1px dashed ${color}` : 'none',
          borderLeft: extendsLeft ? 'none' : `3px solid ${color}`,
          borderRight: extendsRight ? `1px dashed ${color}80` : 'none',
          boxShadow: isHighlighted ? `0 0 0 1.5px ${color}, 0 0 6px ${color}66` : undefined,
          outline: isHighlighted ? `1.5px solid ${color}` : undefined,
          touchAction: draggable ? 'none' : undefined,
          opacity: isDragging ? 0.4 : undefined,
        }}
      >
        <div
          className={cn(
            'flex items-center gap-1 truncate text-xs font-semibold leading-tight',
            (isInstalled || isCompleted) && 'line-through',
          )}
          style={{ color }}
        >
          {isEstimate && (
            <span className="text-[0.625rem] uppercase tracking-wider opacity-80">
              Est
            </span>
          )}
          {isCompleted && (
            <Check className="h-3 w-3 shrink-0" aria-label="completed" />
          )}
          <span className="truncate">{assignment.job.displayName}</span>
          {peopleCount > 1 && (
            <span
              className="ml-auto shrink-0 rounded-sm px-1 text-[0.5625rem] font-bold leading-tight"
              style={{ backgroundColor: `${color}22`, color }}
              title={`${peopleCount} people on this job: ${assignment.people.map((p) => p.name).join(', ')}`}
            >
              ×{peopleCount}
            </span>
          )}
        </div>
        <div
          className={cn(
            'truncate text-[0.625rem] leading-tight text-muted-foreground',
            (isInstalled || isCompleted) && 'line-through',
          )}
        >
          {assignment.job.customerName}
        </div>
      </div>

      {isSelected && (
        <AssignmentEditModal assignment={assignment} onClose={onDeselect} />
      )}
    </>
  );
}
